import React, { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../store/slices/authSlice";
import { RootState } from "../../store";

// Constants for timeout durations
const IDLE_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
const WARNING_BEFORE_MS = 60 * 1000; // 1 minute warning

export const IdleTimer: React.FC = () => {
  const { token, user } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  
  const [showWarning, setShowWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(60);
  const [mounted, setMounted] = useState(false);
  
  const lastActivityRef = useRef<number>(Date.now());
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const logoutTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  const handleLogout = useCallback(() => {
    setShowWarning(false);
    dispatch(logout());
    window.location.href = "/admin/login";
  }, [dispatch]);

  const showWarningRef = useRef(showWarning);
  useEffect(() => {
    showWarningRef.current = showWarning;
  }, [showWarning]);

  const resetTimer = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    
    // Clear existing timers
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);

    if (showWarningRef.current) {
      setShowWarning(false);
      setRemainingTime(60);
    }

    // Only set timers if authenticated and role is admin or superadmin
    const isPrivilegedUser = user && (user.role === "admin" || user.role === "superadmin");
    
    if (token && isPrivilegedUser) {
      const warnAt = IDLE_TIMEOUT_MS - WARNING_BEFORE_MS;
      
      // Set warning timer
      warningTimerRef.current = setTimeout(() => {
        setShowWarning(true);
        startCountdown();
      }, warnAt);

      // Set final logout timer
      logoutTimerRef.current = setTimeout(() => {
        handleLogout();
      }, IDLE_TIMEOUT_MS);
    }
  }, [token, user, handleLogout]);

  const startCountdown = () => {
    setRemainingTime(60);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    
    countdownIntervalRef.current = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    const isPrivilegedUser = user && (user.role === "admin" || user.role === "superadmin");
    
    if (!token || !isPrivilegedUser) {
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
      setShowWarning(false);
      return;
    }

    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];

    const throttledReset = () => {
      if (showWarningRef.current) return;
      const now = Date.now();
      if (now - lastActivityRef.current > 1000) {
        resetTimer();
      }
    };

    events.forEach((event) => {
      window.addEventListener(event, throttledReset);
    });

    resetTimer();

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, throttledReset);
      });
      
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [token, user, resetTimer]);

  if (!mounted || !showWarning) return null;

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 999999, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}>
      <div className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-8 text-center shadow-2xl transition-all" style={{ border: '1px solid #e5e7eb' }}>
        <div className="mb-4 flex justify-center">
          <div className="rounded-full bg-red-100 p-4 text-red-600">
            <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
        </div>
        
        <h3 className="mb-2 text-2xl font-bold text-gray-900">Session Expiring</h3>
        <p className="mb-6 text-gray-600">
          You have been inactive for a while. You will be logged out in{" "}
          <span className="font-bold text-red-600">{remainingTime} seconds</span>.
        </p>

        <div className="flex flex-col gap-3">
          <button
            onClick={() => resetTimer()}
            className="w-full rounded-xl bg-red-600 px-4 py-3 font-semibold text-white shadow-lg shadow-red-200 hover:bg-red-700 transition-all border-none cursor-pointer"
          >
            Stay Logged In
          </button>
          <button
            onClick={handleLogout}
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 font-semibold text-gray-700 hover:bg-gray-100 transition-all cursor-pointer"
          >
            Logout Now
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
